import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { translations } from "../../translations";
import "./ProjectsPage.css";

const projects = [
  {
    id: "realtor-parminder-singh",
    title: "Realtor Parminder Singh",
    subtitle: {
      en: "Real Estate Website",
      hi: "रियल एस्टेट वेबसाइट",
      pa: "ਰੀਅਲ ਅਸਟੇਟ ਵੈੱਬਸਾਈਟ",
      fr: "Site Web Immobilier"
    },
    category: {
      en: "Web Development",
      hi: "वेब डेवलपमेंट",
      pa: "ਵੈੱਬ ਡਿਵੈਲਪਮੈਂਟ",
      fr: "Développement Web"
    },
    url: "https://realtorparmindersingh.ca",
    thumbnail: "/img/projects/realtor-thumb.svg",
    description: {
      en: "A modern, responsive real estate website designed to help clients find their dream homes in Winnipeg.",
      hi: "एक आधुनिक, रेस्पॉन्सिव रियल एस्टेट वेबसाइट जो ग्राहकों को विनीपेग में उनके सपनों का घर खोजने में मदद करती है।",
      pa: "ਇੱਕ ਆਧੁਨਿਕ, ਰਿਸਪੌਂਸਿਵ ਰੀਅਲ ਅਸਟੇਟ ਵੈੱਬਸਾਈਟ ਜੋ ਗਾਹਕਾਂ ਨੂੰ ਵਿਨੀਪੈਗ ਵਿੱਚ ਉਨ੍ਹਾਂ ਦੇ ਸੁਪਨਿਆਂ ਦਾ ਘਰ ਲੱਭਣ ਵਿੱਚ ਮਦਦ ਕਰਦੀ ਹੈ।",
      fr: "Un site web immobilier moderne et responsive conçu pour aider les clients à trouver leur maison de rêve à Winnipeg."
    },
    tags: ["React", "Responsive", "SEO", "Real Estate"],
    featured: true,
    year: "2024",
  },
];

export const ProjectsPage = () => {
  const { language } = useLanguage();
  const t = translations.projects[language] || translations.projects.en;

  return (
    <div className="projects-page">
      {/* Header */}
      <header className="projects-header">
        <div className="container">
          <span className="header-badge">{language === 'en' ? 'Our Portfolio' : language === 'hi' ? 'हमारा पोर्टफोलियो' : language === 'pa' ? 'ਸਾਡਾ ਪੋਰਟਫੋਲੀਓ' : 'Notre Portfolio'}</span>
          <h1>{t.title}</h1>
          <p>{t.subtitle}</p>
        </div>
      </header>

      {/* Stats */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-number">50+</span>
              <span className="stat-label">{t.projectsDelivered}</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">30+</span>
              <span className="stat-label">{t.happyClients}</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">5+</span>
              <span className="stat-label">{t.yearsExperience}</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">100%</span>
              <span className="stat-label">{language === 'en' ? 'Client Satisfaction' : language === 'hi' ? 'क्लाइंट संतुष्टि' : language === 'pa' ? 'ਗਾਹਕ ਸੰਤੁਸ਼ਟੀ' : 'Satisfaction Client'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="projects-grid-section">
        <div className="container">
          <h2>{t.featuredTitle}</h2>
          <div className="projects-grid">
            {projects.map((project) => (
              <Link to={`/projects/${project.id}`} key={project.id} className={`project-card ${project.featured ? 'featured' : ''}`}>
                <div className="project-thumbnail">
                  {/* Device Mockup Preview with Screenshots */}
                  <div className="device-preview">
                    {/* Desktop Mockup */}
                    <div className="preview-desktop">
                      <div className="desktop-frame">
                        <div className="desktop-header">
                          <span className="dot"></span>
                          <span className="dot"></span>
                          <span className="dot"></span>
                        </div>
                        <div className="desktop-screen">
                          <img src="/img/projects/realtor-desktop.png" alt="Desktop View" />
                        </div>
                      </div>
                      <div className="desktop-stand"></div>
                    </div>
                    {/* Mobile Mockup */}
                    <div className="preview-mobile">
                      <div className="mobile-frame">
                        <div className="mobile-notch"></div>
                        <div className="mobile-screen">
                          <img src="/img/projects/realtor-mobile.png" alt="Mobile View" />
                        </div>
                      </div>
                    </div>
                  </div>
                  {project.featured && <span className="featured-badge">{t.featuredTitle}</span>}
                </div>
                <div className="project-info">
                  <span className="project-category">{project.category[language] || project.category.en}</span>
                  <h3>{project.title}</h3>
                  <p className="project-subtitle">{project.subtitle[language] || project.subtitle.en}</p>
                  <p className="project-description">{project.description[language] || project.description.en}</p>
                  <div className="project-tags">
                    {project.tags.slice(0, 3).map((tag, idx) => (
                      <span key={idx} className="tag">{tag}</span>
                    ))}
                  </div>
                  <span className="view-project">{t.viewProject} →</span>
                </div>
              </Link>
            ))}
          </div>

          {/* Coming Soon Note */}
          <div className="more-projects-note">
            <div className="coming-soon-icon">🚀</div>
            <h3>{t.moreProjects}</h3>
            <p>{language === 'en' ? "We're constantly working on exciting new projects. Check back soon to see our latest work!" : 
                language === 'hi' ? "हम लगातार नए प्रोजेक्ट्स पर काम कर रहे हैं। हमारा नवीनतम काम देखने के लिए जल्द वापस आएं!" : 
                language === 'pa' ? "ਅਸੀਂ ਲਗਾਤਾਰ ਨਵੇਂ ਪ੍ਰੋਜੈਕਟਸ 'ਤੇ ਕੰਮ ਕਰ ਰਹੇ ਹਾਂ। ਸਾਡਾ ਨਵੀਨਤਮ ਕੰਮ ਦੇਖਣ ਲਈ ਜਲਦੀ ਵਾਪਸ ਆਓ!" : 
                "Nous travaillons constamment sur de nouveaux projets. Revenez bientôt pour voir notre dernier travail!"}</p>
          </div>
        </div>
      </section>

      {/* Services CTA */}
      <section className="services-cta">
        <div className="container">
          <h2>{t.startProject}</h2>
          <p>{t.startProjectText}</p>
          <div className="services-list">
            <div className="service-item">
              <span className="service-icon">🌐</span>
              <span>{language === 'en' ? 'Web Development' : language === 'hi' ? 'वेब डेवलपमेंट' : language === 'pa' ? 'ਵੈੱਬ ਡਿਵੈਲਪਮੈਂਟ' : 'Développement Web'}</span>
            </div>
            <div className="service-item">
              <span className="service-icon">📱</span>
              <span>{language === 'en' ? 'Mobile Apps' : language === 'hi' ? 'मोबाइल ऐप्स' : language === 'pa' ? 'ਮੋਬਾਈਲ ਐਪਸ' : 'Apps Mobiles'}</span>
            </div>
            <div className="service-item">
              <span className="service-icon">⚙️</span>
              <span>{language === 'en' ? 'Custom Software' : language === 'hi' ? 'कस्टम सॉफ्टवेयर' : language === 'pa' ? 'ਕਸਟਮ ਸਾਫਟਵੇਅਰ' : 'Logiciel Sur Mesure'}</span>
            </div>
            <div className="service-item">
              <span className="service-icon">☁️</span>
              <span>{language === 'en' ? 'Cloud Solutions' : language === 'hi' ? 'क्लाउड सॉल्यूशंस' : language === 'pa' ? 'ਕਲਾਉਡ ਹੱਲ' : 'Solutions Cloud'}</span>
            </div>
          </div>
          <a href="/#contact" className="cta-btn">{language === 'en' ? 'Get a Free Quote' : language === 'hi' ? 'मुफ्त कोट प्राप्त करें' : language === 'pa' ? 'ਮੁਫ਼ਤ ਕੋਟ ਪ੍ਰਾਪਤ ਕਰੋ' : 'Obtenir un Devis Gratuit'}</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="projects-footer">
        <div className="container">
          <p>© 2025 9Reason • {language === 'en' ? 'Software Development Agency' : language === 'hi' ? 'सॉफ्टवेयर डेवलपमेंट एजेंसी' : language === 'pa' ? 'ਸਾਫਟਵੇਅਰ ਡਿਵੈਲਪਮੈਂਟ ਏਜੰਸੀ' : 'Agence de Développement Logiciel'}</p>
          <div className="footer-links">
            <Link to="/">{language === 'en' ? 'Home' : language === 'hi' ? 'होम' : language === 'pa' ? 'ਹੋਮ' : 'Accueil'}</Link>
            <Link to="/community">{language === 'en' ? 'Community' : language === 'hi' ? 'समुदाय' : language === 'pa' ? 'ਕਮਿਊਨਿਟੀ' : 'Communauté'}</Link>
            <a href="/#contact">{language === 'en' ? 'Contact' : language === 'hi' ? 'संपर्क' : language === 'pa' ? 'ਸੰਪਰਕ' : 'Contact'}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};
